import { Button } from "../button"
import { cn } from "../../../utils/cn"
import {
  addDays,
  addWeeks,
  differenceInMinutes,
  isSameHour,
  isToday,
  setHours,
  startOfWeek,
  subWeeks,
} from "date-fns"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { Badge } from "../badge"
import {
  CalendarEvent,
  StudentStatus,
  badgeVariantColors,
  dayEventVariants,
  formatDateToDutch,
  getWebCalendarStatusLabel,
  renderBadgeLabel,
} from "toptaal-shared"
import { CheckIcon } from "@heroicons/react/16/solid"
import { PlusIcon } from "@heroicons/react/24/solid"

const START_HOUR = 8 // Timetable starts at 09:00, but is set on 08:00 to have a empty slot at the start of the timetable
const END_HOUR = 22 // 22:00
const WEEK_STARTS_ON = 1 // Monday

export const useCalendar = () => useContext(Context)

type CalendarType = "static" | "dynamic"

type ContextType = {
  date: Date
  setDate: (date: Date) => void
  events: CalendarEvent[]
  setEvents: (date: CalendarEvent[]) => void
  onEventClick?: (event: CalendarEvent) => void
  showTimeMarker?: boolean
  today: Date
  calendarType: CalendarType
  showStudentAttendance: boolean
}

const Context = createContext<ContextType>({} as ContextType)

type EventGroupProps = {
  events: CalendarEvent[]
  hour: Date
  renderContent: (event: CalendarEvent, time: string) => React.ReactNode
}

type CalendarProps = {
  children?: React.ReactNode
  defaultDate?: Date
  events?: CalendarEvent[]
  showTimeMarker?: boolean
  onEventClick?: (event: CalendarEvent) => void
  calendarType?: CalendarType
  showStudentAttendance?: boolean
}

const FullCalendar = ({
  children,
  defaultDate = new Date(),
  onEventClick,
  events: defaultEvents = [],
  showTimeMarker = true,
  calendarType = "static",
  showStudentAttendance = false,
}: CalendarProps) => {
  const [date, setDate] = useState(defaultDate)
  const [events, setEvents] = useState<CalendarEvent[]>(defaultEvents)

  return (
    <Context.Provider
      value={{
        date,
        setDate,
        events,
        setEvents,
        onEventClick,
        showTimeMarker,
        today: new Date(),
        calendarType,
        showStudentAttendance,
      }}
    >
      <div className="flex flex-col">{children}</div>
    </Context.Provider>
  )
}

const EventGroup = ({ events, hour, renderContent }: EventGroupProps) => {
  const { onEventClick } = useCalendar()

  return (
    <div className="h-12 border-t last:border-b border-light-grey last:12">
      {events
        .filter((event) => isSameHour(event.start, hour))
        .map((event) => {
          const hoursDifference =
            differenceInMinutes(event.end, event.start) / 60
          const startPosition = event.start.getMinutes() / 60
          const time =
            formatDateToDutch(event.start, { format: "time" }) +
            " - " +
            formatDateToDutch(event.end, { format: "time" })

          return (
            <div
              key={event.id}
              className={cn(
                "relative cursor-pointer hover:opacity-80 transition-opacity overflow-hidden",
                dayEventVariants({ variant: event.eventType }),
                event.status === "cancelled" && "opacity-50 hover:opacity-50",
              )}
              style={{
                top: `${startPosition * 100}%`,
                height: `${hoursDifference * 100}%`,
              }}
              onClick={() => onEventClick?.(event)}
            >
              <div className="flex flex-col justify-between h-full">
                <div className="flex flex-col gap-1 h-full">
                  {renderContent(event, time)}
                </div>
              </div>
            </div>
          )
        })}
    </div>
  )
}

const AvailabilityGroup = ({
  events,
  hour,
}: {
  events: CalendarEvent[]
  hour: Date
}) => {
  return (
    <EventGroup
      events={events}
      hour={hour}
      renderContent={(event, time) => (
        <>
          <p className="font-normal text-base">{event.title}</p>
          <p className="font-normal">{time}</p>
        </>
      )}
    />
  )
}

const LessonGroup = ({
  events,
  hour,
}: {
  events: CalendarEvent[]
  hour: Date
}) => {
  const { showStudentAttendance } = useCalendar()

  return (
    <EventGroup
      events={events}
      hour={hour}
      renderContent={(event, time) => {
        const { type, title, classroom, lessonNumber, status } = event

        let studentStatus: StudentStatus | undefined
        if (
          type === "intake" &&
          "intakeStudents" in event &&
          Array.isArray(event.intakeStudents)
        ) {
          studentStatus = event.intakeStudents[0]?.status
        } else if (
          type === "lesson" &&
          "lessonStudents" in event &&
          Array.isArray(event.lessonStudents)
        ) {
          studentStatus = event.lessonStudents[0]?.status
        }

        const { text, icon, color } = getWebCalendarStatusLabel(
          studentStatus ?? "registered",
        )

        const showCompletedStatus =
          !showStudentAttendance && event.presenceRegistered
        const showRegularStatus = !showCompletedStatus && studentStatus

        return (
          <div className="flex flex-col h-full justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <Badge
                  className={cn(
                    badgeVariantColors({ variant: event.eventType }),
                  )}
                >
                  {renderBadgeLabel(event.eventType)}
                </Badge>

                {lessonNumber && (
                  <p className="text-xs font-normal capitalize text-primary-dark-blue/80">
                    Les {lessonNumber}
                  </p>
                )}
              </div>

              <p className="text-xs font-bold">{time}</p>

              <p className="text-xs font-normal truncate capitalize">{title}</p>

              {classroom && (
                <p className="text-xs font-normal truncate capitalize">
                  {classroom}
                </p>
              )}
            </div>

            {showCompletedStatus && (
              <div className="flex justify-end">
                <p className="text-xs font-bold capitalize flex items-center gap-1 text-secondary-mint-green">
                  afgerond
                  <CheckIcon width={16} height={16} />
                </p>
              </div>
            )}

            {showRegularStatus && (
              <div className="flex justify-end">
                <p
                  className={cn(
                    "text-xs font-bold capitalize flex items-center gap-1",
                    color,
                  )}
                >
                  {text}
                  {icon}
                </p>
              </div>
            )}

            {status === "cancelled" && (
              <div className="flex justify-end">
                <p className="text-xs font-bold capitalize text-red-500 flex items-center gap-1">
                  Vervallen
                  <PlusIcon width={16} height={16} className="rotate-45" />
                </p>
              </div>
            )}
          </div>
        )
      }}
    />
  )
}

const CalendarView = () => {
  const { date, events, calendarType, showTimeMarker } = useCalendar()
  const now = useMemo(() => new Date(), [])

  const weekDates = useMemo(() => {
    const start = startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON })
    const weekDates = []

    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i)
      const hours = Array.from({ length: END_HOUR - START_HOUR }).map((_, i) =>
        setHours(day, START_HOUR + i),
      )
      weekDates.push(hours)
    }

    return weekDates
  }, [date])

  const headerDays = useMemo(() => {
    const daysOfWeek = []
    for (let i = 0; i < 7; i++) {
      const result = addDays(
        startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON }),
        i,
      )
      daysOfWeek.push(result)
    }

    return daysOfWeek
  }, [date])

  return (
    <div className="flex flex-col relative overflow-auto max-h-screen">
      <div className="flex sticky top-0 bg-card z-10">
        <div className="w-12"></div>

        {headerDays.map((date, index) => (
          <div
            key={date.toString()}
            className={cn(
              "text-center flex-1 gap-1 pb-2 text-sm flex items-center justify-center border-r-1 border-light-grey capitalize",
              {
                "border-l-1": index === 0,
              },
            )}
          >
            {formatDateToDutch(date, { format: "weekdayLong" })}

            {calendarType === "dynamic" && (
              <span
                className={cn(
                  "h-6 grid place-content-center",
                  isToday(date) &&
                    "bg-primary text-primary-foreground rounded-full size-6 font-bold",
                )}
              >
                {formatDateToDutch(date, { format: "day" })}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-1">
        <div className="w-fit">
          <TimeTable />
        </div>

        <div className="grid grid-cols-7 flex-1">
          {weekDates.map((hours, colIndex) => {
            const dayDate = headerDays[colIndex]
            // totaal minuten in je window (08:00–22:00)
            const totalMinutes = (END_HOUR - START_HOUR) * 60 // 14*60 = 840
            // hoeveel minuten sinds START_HOUR nu voorbij zijn
            const minutesSinceStart =
              (now.getHours() - START_HOUR) * 60 + now.getMinutes()
            // positie als % van de 14-uurs window
            const topPercent = (minutesSinceStart / totalMinutes) * 100

            return (
              <div
                key={dayDate.toISOString()}
                className={cn(
                  "relative text-sm border-l first:border-l-1 last:border-r-1 border-light-grey self-start",
                  { "bg-primary-accent-blue": isToday(dayDate) },
                )}
              >
                {showTimeMarker && isToday(dayDate) && (
                  <div
                    className="absolute inset-x-0 h-[2px] bg-red-500 z-10"
                    style={{ top: `${topPercent}%` }}
                  >
                    <div className="size-2 rounded-full bg-red-500 absolute left-0 top-1/2 -translate-y-1/2" />
                  </div>
                )}

                {hours.map((hour) => {
                  const Component =
                    calendarType === "dynamic" ? LessonGroup : AvailabilityGroup

                  return (
                    <Component
                      key={hour.toString()}
                      hour={hour}
                      events={events}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function CalendarNextTrigger({
  children,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { date, setDate } = useCalendar()
  const next = useCallback(() => setDate(addWeeks(date, 1)), [date, setDate])

  return (
    <Button
      variant="ghost"
      {...props}
      onClick={(e) => {
        next()
        onClick?.(e)
      }}
      className="p-2"
    >
      {children}
    </Button>
  )
}

function CalendarPrevTrigger({
  children,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { date, setDate } = useCalendar()
  const prev = useCallback(() => setDate(subWeeks(date, 1)), [date, setDate])

  return (
    <Button
      variant="ghost"
      {...props}
      onClick={(e) => {
        prev()
        onClick?.(e)
      }}
      className="p-2"
    >
      {children}
    </Button>
  )
}

function CalendarTodayTrigger({
  children,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { setDate, today } = useCalendar()
  const jumpToToday = useCallback(() => setDate(today), [today, setDate])

  return (
    <Button
      variant="ghost"
      {...props}
      onClick={(e) => {
        jumpToToday()
        onClick?.(e)
      }}
    >
      {children}
    </Button>
  )
}

const CalendarCurrentDate = () => {
  const { date } = useCalendar()

  return (
    <time
      dateTime={date.toISOString()}
      className="!font-domine font-bold text-base capitalize"
    >
      {formatDateToDutch(date, { format: "monthLongYear" })}
    </time>
  )
}

const TimeTable = () => {
  return (
    <div className="pr-2 w-12">
      {Array.from({ length: END_HOUR - START_HOUR + 1 }).map((_, idx) => {
        const hour = START_HOUR + idx

        return (
          <div
            key={hour}
            className="text-right relative text-xs text-muted-foreground/50 h-12 last:h-12"
          >
            {hour !== START_HOUR && (
              <p className="top-0 -translate-y-1/2">{hour}:00</p>
            )}
          </div>
        )
      })}
    </div>
  )
}

export {
  FullCalendar,
  CalendarCurrentDate,
  CalendarNextTrigger,
  CalendarPrevTrigger,
  CalendarTodayTrigger,
  CalendarView,
}
